import { useCallback, useEffect, useState } from 'react';
import React from 'react';
import { Icon } from '@monorepo/base/src/components/icon/icon';
import { Menu } from '@monorepo/base/src/components/menu/menu';
import { Spacer } from '@monorepo/base/src/components/spacer/spacer';
import { NotificationsApi, NotificationMessage } from '../../apis/notifications.api';
import { NotificationItem } from './notification-item';
import styles from './notification-center.module.scss';
import routes from '../../routes/index.routes';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

const PAGE_SIZE = 5;

export const NotificationCenter = () => {
	const [notifications, setNotifications] = useState<NotificationMessage[]>([]);
	const [isOpenMenu, setIsOpenMenu] = useState(false);
	const [page, setPage] = useState(1);
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const accountId = searchParams.get('accountId');

	const fetchList = useCallback(() => {
		NotificationsApi.list(accountId).then(list => setNotifications(list));
	}, [accountId]);

	useEffect(() => {
		fetchList();
	}, [fetchList]);

	const handleMarkAsRead = useCallback(
		(id: number) => {
			NotificationsApi.markAsRead(id, accountId).then(({ marked }) => {
				if (marked) {
					setNotifications(prev => prev.map(n => (n.id === id ? { ...n, read: true } : n)));
				}
			});
		},
		[accountId]
	);

	const handleNotificationClick = useCallback(
		(notification: NotificationMessage) => {
			if (!notification.read) {
				handleMarkAsRead(notification.id);
			}

			setIsOpenMenu(false);
			if (location.pathname !== routes.general.notifications()) {
				navigate(routes.general.notifications());
			}
		},
		[handleMarkAsRead, location.pathname, navigate]
	);

	const handleMarkAllAsRead = useCallback(() => {
		NotificationsApi.markAllAsRead(accountId).then(({ marked }) => {
			if (marked > 0) {
				setNotifications(prev => prev.map(n => ({ ...n, read: true })));
			}
		});
	}, [accountId]);

	const unreadCount = notifications.filter(n => !n.read).length;
	const totalPages = Math.max(1, Math.ceil(notifications.length / PAGE_SIZE));
	useEffect(() => {
		if (page > totalPages && totalPages >= 1) {
			setPage(totalPages);
		}
	}, [totalPages, page]);

	const currentPage = Math.min(page, totalPages);
	const start = (currentPage - 1) * PAGE_SIZE;
	const paginatedNotifications = notifications.slice(start, start + PAGE_SIZE);

	const goToPrevPage = () => setPage(p => Math.max(1, p - 1));
	const goToNextPage = () => setPage(p => Math.min(totalPages, p + 1));

	const NotificationBar = () => (
		<div className={styles.notificationBellWrapper}>
			<Icon
				className={styles.notificationBell}
				color="#8a8a8a"
				size="20px"
				isMFP={true}
				onClick={() => {
					setPage(1);
					setIsOpenMenu(!isOpenMenu);
				}}>
				bell-03
			</Icon>
			{unreadCount > 0 && (
				<span className={styles.notificationBadge} aria-label={`${unreadCount} unread notifications`}>
					{unreadCount > PAGE_SIZE ? PAGE_SIZE + '+' : unreadCount}
				</span>
			)}
		</div>
	);

	return (
		<div className={styles.wrapper}>
			<NotificationBar />
			<Menu isOpen={isOpenMenu} onClose={() => setIsOpenMenu(false)} alignToRight={true} className={styles.menu}>
				<div className={styles.notificationsMenu}>
					<div className={styles.title}>Notifications</div>
					{unreadCount > 0 && (
						<React.Fragment>
							<button type="button" className={styles.markAllRead} onClick={handleMarkAllAsRead}>
								Mark all as read
							</button>
							<Spacer y={1} />
						</React.Fragment>
					)}
					{!notifications || notifications.length === 0 ? (
						<React.Fragment>
							<Spacer y={2} />
							<div className={styles.noting}>Nothing new here</div>
						</React.Fragment>
					) : (
						<React.Fragment>
							{paginatedNotifications.map((notification, index) => (
								<React.Fragment key={notification.id}>
									<NotificationItem notification={notification} onClick={handleNotificationClick} />
									{index + 1 !== paginatedNotifications.length ? <Spacer y={2} /> : null}
								</React.Fragment>
							))}
							{totalPages > 1 && (
								<React.Fragment>
									<Spacer y={2} />
									<div className={styles.pagination}>
										<span className={styles.paginationInfo}>
											{start + 1}-{start + paginatedNotifications.length} of {notifications.length}
										</span>
										<div className={styles.paginationButtons}>
											<button
												type="button"
												className={styles.paginationBtn}
												onClick={goToPrevPage}
												disabled={currentPage <= 1}
												aria-label="Previous page">
												Previous
											</button>
											<button
												type="button"
												className={styles.paginationBtn}
												onClick={goToNextPage}
												disabled={currentPage >= totalPages}
												aria-label="Next page">
												Next
											</button>
										</div>
									</div>
								</React.Fragment>
							)}
						</React.Fragment>
					)}
				</div>
			</Menu>
		</div>
	);
};
